/**
 * CaseVendor DTOs
 *
 * Zod schemas and TypeScript types for closing vendors attached to a case
 * (title companies, escrow, insurance, appraisers, GCs).
 *
 * Issue #1470 — normalised replacement for the `Case.vendors Json?` column
 * shipped in PR #1492 / #1496.
 *
 * JSON-vs-row schema drift (vs. the legacy `caseVendorsSchema` JSON-column shape):
 *
 *   Legacy JSON: { closer?, escrow?, title?, appraiser?, attorney?, custom? }
 *                — string IDs/names only.
 *   New row:     vendorRole ∈ { TITLE_COMPANY, ESCROW_OFFICER, INSURANCE_PROVIDER,
 *                APPRAISER, GC_BUILDER, OTHER } + full contact + amount data.
 *
 *   Legacy → row mapping:
 *     title      -> TITLE_COMPANY     (1:1)
 *     escrow     -> ESCROW_OFFICER    (1:1)
 *     appraiser  -> APPRAISER         (1:1)
 *     closer     -> OTHER             (with vendor name as `companyName`/`notes`)
 *     attorney   -> OTHER             (with vendor name as `companyName`/`notes`)
 *     custom.K   -> OTHER             (key K in `notes` as the role label)
 *
 *   Net-new typed roles (no legacy equivalent):
 *     INSURANCE_PROVIDER, GC_BUILDER — captured here for the first time.
 *
 *   Reverse direction (row → JSON for backwards-compat reads): collapse all
 *   contact fields to the company/contact name string; drop INSURANCE_PROVIDER
 *   and GC_BUILDER since the legacy shape can't represent them; merge multiple
 *   OTHER rows into `custom.{notes}` keyed by the row's notes/role label.
 *   This is intentionally lossy — the JSON column is a fallback, not a source
 *   of truth, and is dropped in a follow-up migration.
 */

import { z } from 'zod';
import { vendorRoleSchema } from '../enums/case-vendor.enums';
import { moneyString } from './common.dto';

// ============================================================================
// CASE VENDOR
// ============================================================================

/**
 * Common contact-and-detail fields shared by Create + Update.
 *
 * `companyName`, `contactName`, `email`, `phone` — basic vendor contact info.
 * `orderNumber` — title order #, appraisal order #, etc.
 * `policyNumber` — insurance policy #.
 * `amount` — insurance amount, appraisal value, etc. Stored as Decimal(14,2);
 *            sent over the wire as a string to preserve precision.
 *            Non-negative for both numeric and string inputs.
 * `notes` — free-form text.
 */
const VendorContactFieldsSchema = z.object({
  companyName: z.string().trim().max(200).optional(),
  contactName: z.string().trim().max(200).optional(),
  email: z
    .string()
    .email()
    .max(320)
    .optional()
    .or(z.literal('').transform(() => undefined)),
  phone: z.string().trim().max(40).optional(),
  orderNumber: z.string().trim().max(120).optional(),
  policyNumber: z.string().trim().max(120).optional(),
  /**
   * Decimal serialized as a string; numeric input is also accepted.
   * Non-negative on BOTH branches (numeric uses `.nonnegative()`, string regex
   * disallows the leading minus). Keeping the two branches consistent prevents
   * sign-mismatch bugs where `amount: -100` would reject but `amount: '-100'`
   * would pass.
   */
  amount: z.union([z.number().finite().nonnegative(), moneyString()]).optional(),
  notes: z.string().max(4000).optional(),
});

/** Schema for creating a CaseVendor row. */
export const CreateCaseVendorInputSchema = VendorContactFieldsSchema.extend({
  caseId: z.string().uuid(),
  vendorRole: vendorRoleSchema,
});
export type CreateCaseVendorInput = z.infer<typeof CreateCaseVendorInputSchema>;

/**
 * Schema for partial update of a CaseVendor row.
 *
 * `caseId` and `vendorRole` are intentionally omitted: changing the case
 * effectively means "delete + create elsewhere", and changing the role would
 * collide with the partial-unique constraint and break audit attribution.
 * If callers need either, they should soft-delete + create a new row.
 *
 * At least one field must be provided — empty patches are rejected to match
 * the convention in `UpdateCaseSchema` / `UpdateBusinessSchema` and avoid
 * no-op writes that still emit audit-log noise.
 */
export const UpdateCaseVendorInputSchema = VendorContactFieldsSchema.partial().refine(
  (data) => Object.keys(data).length > 0,
  { message: 'At least one field must be provided for update' },
);
export type UpdateCaseVendorInput = z.infer<typeof UpdateCaseVendorInputSchema>;

/**
 * Schema for listing CaseVendors. Always scoped to a single case (per the
 * row-per-case design — vendors are not browsed cross-case from the UI). The
 * service layer adds the implicit `businessId` filter from the auth context.
 *
 * `vendorRole` filter is optional — pass it to fetch a specific role (e.g.
 * "give me the title company on this case"). `includeDeleted` defaults to
 * false to honour the soft-delete contract; admin tooling can opt in.
 */
export const ListCaseVendorsInputSchema = z.object({
  caseId: z.string().uuid(),
  vendorRole: vendorRoleSchema.optional(),
  includeDeleted: z.boolean().default(false),
});
export type ListCaseVendorsInput = z.infer<typeof ListCaseVendorsInputSchema>;

/** Full CaseVendor response shape. */
export const CaseVendorSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  caseId: z.string().uuid(),
  vendorRole: vendorRoleSchema,
  companyName: z.string().nullable().optional(),
  contactName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  orderNumber: z.string().nullable().optional(),
  policyNumber: z.string().nullable().optional(),
  /** Decimal as string (avoids precision loss across the wire). */
  amount: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  /** ISO string when soft-deleted; null/undefined when active. */
  deletedAt: z.string().datetime().nullable().optional(),
});
export type CaseVendorDto = z.infer<typeof CaseVendorSchema>;
